import React, { useEffect, useRef } from 'react';
import './AtacamaExperiencesSection.css';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { images } from '../shared/assets';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../shared/CTALink/CTALink';

// Atacama experience images
const experience1Image = "/assets/images/atacama/organized/landscape/atacama-landscape-view-15.png";
const experience2Image = "/assets/images/atacama/organized/landscape/atacama-landscape-view-25.png";
const experience3Image = "/assets/images/atacama/organized/landscape/atacama-landscape-view-35.png";
const experience4Image = "/assets/images/atacama/organized/landscape/atacama-landscape-view-45.png";
const experience5Image = "/assets/images/atacama/organized/landscape/atacama-landscape-view-55.png";

interface ExperienceCard {
  id?: number;
  image: string;
  tag?: string;
  name?: string;
  title?: string;
  location?: string;
  category?: string;
  description?: string;
}

interface AtacamaExperiencesSectionProps {
  property?: string;
  title?: string;
  description?: string;
  experiences?: ExperienceCard[];
  restaurantTitle?: string;
  restaurantDescription?: string;
}

// Tag Component - Match GlacierVideoExploration
const Tag: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div className="atacama-experience-tag">
      <div className="atacama-experience-tag-text">
        <p>{text}</p>
      </div>
    </div>
  );
};

// Experience Card Component - Match GlacierVideoExploration
const ExperienceCard: React.FC<{ experience: ExperienceCard }> = ({ experience }) => {
  const displayName = experience.name || experience.title || '';
  const displayLocation = experience.location || experience.category || '';
  const displayTag = experience.tag || experience.category || 'EXPERIENCE';
  const displayDescription = experience.description || '';
  
  return (
    <div className="atacama-experience-card">
      <div className="atacama-experience-image-container">
        <img
          src={experience.image}
          alt={`${displayName} ${displayLocation}`}
          className="atacama-experience-image"
        />
        <Tag text={displayTag} />
      </div>
      <div className="atacama-experience-content">
        <div className="atacama-experience-card-title">
          <p>{displayName}</p>
        </div>
        {displayLocation && (
          <p className="atacama-experience-card-category">
            ATACAMA - {displayLocation}
          </p>
        )}
        {displayDescription && (
          <p className="atacama-experience-card-description">
            {displayDescription}
          </p>
        )}
        <div className="atacama-experience-card-actions">
          <CTALink 
            text="CONTACT US"
            type="primary"
            color="awasi"
            href="/contact"
            className="atacama-experience-link"
          />
        </div>
      </div>
    </div>
  );
};

export const AtacamaExperiencesSection: React.FC<AtacamaExperiencesSectionProps> = ({ 
  property = "atacama",
  title = "Experience Your Own Path Private Excursions",
  description = "Since opening in 2007, we have continued to offer memorable tailor-made experiences, sharing with our guests the culture, history and landscape of the Atacama Desert. Visit volcanoes, geysers and salt flats, and be amazed seeing how the landscape magically changes according to the light.",
  experiences: customExperiences
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize drag scroll for the experiences carousel
    const cleanup = initializeDragScroll('atacama-experiences-carousel');
    return cleanup;
  }, []);

  const handleSeeDestinations = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleArrowClick = () => {
    if (carouselRef.current) {
      // Scroll to the right by one card width (1000px + 20px gap)
      const scrollAmount = 1020;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Default experiences data matching the screenshot
  const defaultExperiences: ExperienceCard[] = [
    {
      id: 1,
      image: experience1Image,
      tag: "LOREM IPSUM",
      name: "Chaxa",
      location: "ATACAMA",
      category: "ATACAMA",
      description: "Chaxa Lagoon is notable for its expansive views of the Atacama basin. The main attractions include the salt flat formations and a diverse variety of birdlife; particularly flamingos, which can be observed depending on the season."
    },
    {
      id: 2,
      image: experience2Image,
      tag: "LOREM IPSUM", 
      name: "Rainbow Valley",
      location: "ATACAMA",
      category: "ATACAMA",
      description: "Rainbow Valley is a paradise for geology lovers, it stands out for its characteristic landscapes of the Domeyko mountain range, unique colours and a great variety of activity options, including hiking and viewing petroglyphs of archeological and historical significance."
    },
    {
      id: 3,
      image: experience3Image,
      tag: "LOREM IPSUM",
      name: "Horseback Riding", 
      location: "ATACAMA",
      category: "ATACAMA",
      description: "Explore the world's driest desert on horseback. This excursion takes you through some of Atacama's iconic landscapes: the Valle de Marte, the formations of la Cordillera de la Sal, or the historic Ayllus. The route will be selected based on your riding experience and personal preferences."
    },
    {
      id: 4,
      image: experience4Image,
      tag: "LOREM IPSUM",
      name: "Astronomical Observation",
      location: "ATACAMA", 
      category: "ATACAMA",
      description: "The stargazing tour offers a detailed look at the night sky, under some of the most favorable conditions on earth, both with our bare eyes and through a powerful telescope. You will be amazed by the beauty of space while having the opportunity to learn about subjects such as constellations, the celestial sphere, archeoastronomy, cosmology, stellar evolution and space exploration."
    },
    {
      id: 5,
      image: experience5Image,
      tag: "LOREM IPSUM",
      name: "Choose your pace",
      location: "ATACAMA",
      category: "ATACAMA", 
      description: "You may choose between full-day excursions with lunch included ( from 4 to 6 hrs.); or two half-day excursions (each one between 2 and 5 hrs.) returning to have lunch at Awasi after the first excursion and heading out again in the afternoon."
    }
  ];

  const experiences = customExperiences || defaultExperiences;

  // Use the arrow image from assets
  const arrowImageUrl = images.carouselArrow;

  return (
    <section className="atacama-experiences-section">
      {/* Meaningful Exploration Section Only */}
      <div className="atacama-experiences-content">
        <div className="atacama-experiences-text-content">
          <h2 className="atacama-experiences-title">
            {title.split(' ').map((word, index) => (
              <React.Fragment key={index}>
                {word}{' '}
                {index === 3 && <br />}
              </React.Fragment>
            ))}
          </h2>
          <div className="atacama-experiences-description-wrapper">
            <p className="atacama-experiences-description">
              {description}
            </p>
            <HandDrawnButton
              text="LEARN MORE"
              onClick={handleSeeDestinations}
              variant={property as any}
              color="awasi"
              animation="stroke-to-fill"
              size="normal"
              className="atacama-experiences-learn-button"
            />
          </div>
        </div>

        {/* Carousel Section */}
        <div className="atacama-experiences-carousel-container">
          <div 
            className="atacama-experiences-carousel"
            ref={carouselRef}
            id="atacama-experiences-carousel"
          >
            {experiences.map((experience) => (
              <ExperienceCard key={experience.id} experience={experience} />
            ))}
          </div>
          
          {/* Hand-drawn Arrow - positioned to indicate carousel scroll */}
          <div className="atacama-experiences-arrow" onClick={handleArrowClick}>
            <img 
              src={arrowImageUrl} 
              alt="Scroll to see more experiences" 
              className="atacama-experiences-arrow-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtacamaExperiencesSection;